import { faStar as faRegularStar } from '@fortawesome/free-regular-svg-icons';
import { faStar as faSolidStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Linking, Platform, Pressable, ScrollView, TextInput, View, useWindowDimensions } from 'react-native';
import {
  AppReviewPromptClient,
  AppReviewPromptEventType,
  AppReviewPromptFeedbackReason,
  AppReviewPromptPublicReviewDestination,
  RecordAppReviewPromptEventRequest,
  SubmitAppReviewPromptFeedbackRequest,
} from '../../../api/api.g';
import useApi from '../../../hooks/api-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import {
  getAppReviewPromptPlatform,
  getReviewUrlByDestination,
  openReviewDestination,
} from '../../../helpers/appReviewPromptHelper';
import CustomButton from '../CustomButton';
import CustomCheckbox from '../CustomCheckbox';
import Label from '../Label';

export interface AppReviewPromptPopupProps {
  promptId: string;
  onClose(): void;
}

type AppReviewPromptStep = 'rating' | 'feedback' | 'positive' | 'feedbackSent';

interface FeedbackReasonOption {
  value: AppReviewPromptFeedbackReason;
  labelKey: string;
}

const feedbackReasonOptions: FeedbackReasonOption[] = [
  { value: AppReviewPromptFeedbackReason.MissionInstructionsUnclear, labelKey: 'app-review-feedback-option-instructions' },
  { value: AppReviewPromptFeedbackReason.PaymentIssues, labelKey: 'app-review-feedback-option-payment' },
  { value: AppReviewPromptFeedbackReason.AppProblemOrBug, labelKey: 'app-review-feedback-option-bug' },
  { value: AppReviewPromptFeedbackReason.NotEnoughMissionsNearby, labelKey: 'app-review-feedback-option-missions' },
  { value: AppReviewPromptFeedbackReason.Other, labelKey: 'app-review-feedback-option-other' },
];

const AppReviewPromptPopup = (props: AppReviewPromptPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const api = useApi(AppReviewPromptClient);
  const platform = useMemo(() => getAppReviewPromptPlatform(), []);
  const seenRef = useRef(false);

  const [step, setStep] = useState<AppReviewPromptStep>('rating');
  const [rating, setRating] = useState(0);
  const [selectedReasons, setSelectedReasons] = useState<AppReviewPromptFeedbackReason[]>([]);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const popupWidth = Math.min(390, Math.max(layout.width - 30, 300));
  const popupMaxHeight = Math.max(layout.height - 40, 280);
  const isCompact = popupWidth < 340;
  const accentColor = theme.isDark ? theme.text.darkTurquoiseTwo.toString() : '#43a49e';
  const bodyColor = theme.text.primary.toString();
  const titleColor = theme.text.darkTurquoiseTwo.toString();
  const cardBackgroundColor = theme.background.modal.toString();
  const mutedColor = theme.inputTextPlaceholder?.toString() ?? '#989898';
  const feedbackReasonBoxWidth = 20;
  const feedbackReasonGap = 10;
  const feedbackReasonLabelWidth = popupWidth - (isCompact ? 44 : 56) - feedbackReasonBoxWidth - feedbackReasonGap;

  useEffect(() => {
    if (seenRef.current) {
      return;
    }

    seenRef.current = true;
    api.markSeen(props.promptId, api.tokenSource?.token).catch(() => undefined);
  }, [api, props.promptId]);

  const recordDismissed = useCallback(async () => {
    await api
      .recordEvent(
        props.promptId,
        RecordAppReviewPromptEventRequest.fromJS({
          eventType: AppReviewPromptEventType.Dismissed,
          rating: rating || undefined,
          selectedReasons,
          comment: comment.trim() || undefined,
          platform,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
  }, [api, comment, platform, props.promptId, rating, selectedReasons]);

  const handleClose = useCallback(async () => {
    await recordDismissed();
    props.onClose();
  }, [props, recordDismissed]);

  const recordRatingSubmitted = async () => {
    await api
      .recordEvent(
        props.promptId,
        RecordAppReviewPromptEventRequest.fromJS({
          eventType: AppReviewPromptEventType.RatingSubmitted,
          rating,
          platform,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
  };

  const handleRatingSubmit = async () => {
    if (!rating || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    await recordRatingSubmitted();
    setIsSubmitting(false);
    setStep(rating <= 3 ? 'feedback' : 'positive');
  };

  const handleReasonChange = (checked: boolean, reason: AppReviewPromptFeedbackReason) => {
    setSelectedReasons((currentReasons) => {
      if (checked && !currentReasons.includes(reason)) {
        return [...currentReasons, reason];
      }

      if (!checked) {
        return currentReasons.filter((currentReason) => currentReason !== reason);
      }

      return currentReasons;
    });
  };

  const handleSendFeedback = async () => {
    if (isSubmitting) {
      return;
    }

    const feedbackComment = comment.trim() || undefined;

    setIsSubmitting(true);
    await api
      .submitFeedback(
        props.promptId,
        SubmitAppReviewPromptFeedbackRequest.fromJS({
          rating,
          selectedReasons,
          comment: feedbackComment,
          platform,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
    await api
      .recordEvent(
        props.promptId,
        RecordAppReviewPromptEventRequest.fromJS({
          eventType: AppReviewPromptEventType.FeedbackSent,
          rating,
          selectedReasons,
          comment: feedbackComment,
          platform,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
    setIsSubmitting(false);
    setStep('feedbackSent');
  };

  const handleContactSupport = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    await api
      .recordEvent(
        props.promptId,
        RecordAppReviewPromptEventRequest.fromJS({
          eventType: AppReviewPromptEventType.ContactSupportClicked,
          rating,
          selectedReasons,
          comment: comment.trim() || undefined,
          platform,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
    setIsSubmitting(false);
    Linking.openURL(t('lanla_website_support')).catch(() => undefined);
    props.onClose();
  };

  const handlePublicReview = async (destination: AppReviewPromptPublicReviewDestination) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    await api
      .recordEvent(
        props.promptId,
        RecordAppReviewPromptEventRequest.fromJS({
          eventType: AppReviewPromptEventType.PublicReviewClicked,
          rating,
          selectedReasons,
          comment: comment.trim() || undefined,
          platform,
          destination,
        }),
        api.tokenSource?.token,
      )
      .catch(() => undefined);
    setIsSubmitting(false);
    openReviewDestination(destination).catch(() => undefined);
    props.onClose();
  };

  const renderTitle = (title: string, numberOfLines = 3) => (
    <Label style={{ color: titleColor, fontSize: isCompact ? 24 : 27, lineHeight: isCompact ? 29 : 32, fontWeight: '700' }} numberOfLines={numberOfLines}>
      {title}
    </Label>
  );

  const renderBody = (body: string, numberOfLines = 6) => (
    <Label style={{ marginTop: 16, color: bodyColor, fontSize: isCompact ? 15 : 16, lineHeight: isCompact ? 21 : 23 }} numberOfLines={numberOfLines}>
      {body}
    </Label>
  );

  const renderStarRating = () => (
    <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 22, marginBottom: 10 }}>
      {[1, 2, 3, 4, 5].map((starValue) => (
        <Pressable
          key={starValue}
          accessibilityRole="button"
          accessibilityLabel={`${starValue}`}
          onPress={() => setRating(starValue)}
          style={{ padding: 4 }}
        >
          <FontAwesomeIcon icon={starValue <= rating ? faSolidStar : faRegularStar} size={34} color={accentColor} />
        </Pressable>
      ))}
    </View>
  );

  const renderReviewButtons = () => {
    const appStoreUrl = getReviewUrlByDestination(AppReviewPromptPublicReviewDestination.AppStore);
    const googlePlayUrl = getReviewUrlByDestination(AppReviewPromptPublicReviewDestination.GooglePlay);

    if (Platform.OS === 'ios') {
      return (
        <CustomButton
          title={t('app-review-review-app-store')}
          onPress={() => handlePublicReview(AppReviewPromptPublicReviewDestination.AppStore)}
          disabled={isSubmitting || !appStoreUrl}
          style={{ width: '100%', marginTop: 20 }}
        />
      );
    }

    if (Platform.OS === 'android') {
      return (
        <CustomButton
          title={t('app-review-review-google-play')}
          onPress={() => handlePublicReview(AppReviewPromptPublicReviewDestination.GooglePlay)}
          disabled={isSubmitting || !googlePlayUrl}
          style={{ width: '100%', marginTop: 20 }}
        />
      );
    }

    return (
      <View style={{ gap: 12, marginTop: 20 }}>
        <CustomButton
          title={t('app-review-review-app-store')}
          onPress={() => handlePublicReview(AppReviewPromptPublicReviewDestination.AppStore)}
          disabled={isSubmitting || !appStoreUrl}
          style={{ width: '100%' }}
        />
        <CustomButton
          title={t('app-review-review-google-play')}
          onPress={() => handlePublicReview(AppReviewPromptPublicReviewDestination.GooglePlay)}
          disabled={isSubmitting || !googlePlayUrl}
          style={{ width: '100%' }}
        />
      </View>
    );
  };

  const renderRatingStep = () => (
    <>
      {renderTitle(t('app-review-rating-title'))}
      {renderBody(t('app-review-rating-body'))}
      {renderStarRating()}
      <View style={{ gap: 12, marginTop: 18 }}>
        <CustomButton title={t('app-review-submit')} onPress={handleRatingSubmit} disabled={!rating || isSubmitting} style={{ width: '100%' }} />
        <CustomButton
          secondaryColorLightOutline
          title={t('app-review-not-now')}
          onPress={handleClose}
          disabled={isSubmitting}
          style={{ width: '100%' }}
        />
      </View>
    </>
  );

  const renderFeedbackStep = () => (
    <>
      {renderTitle(t('app-review-feedback-title'))}
      {renderBody(t('app-review-feedback-body'), 7)}
      <View style={{ gap: 12, marginTop: 20 }}>
        {feedbackReasonOptions.map((option) => (
          <CustomCheckbox
            key={option.value}
            value={option.value}
            checked={selectedReasons.includes(option.value)}
            label={t(option.labelKey)}
            color={accentColor}
            markerColor={accentColor}
            labelNumberOfLines={4}
            textStyle={{ color: bodyColor, fontSize: 14, lineHeight: 18, flexGrow: 0, flexShrink: 1, width: feedbackReasonLabelWidth }}
            boxStyle={{ flexGrow: 0, flexShrink: 0, marginTop: 1 }}
            style={{
              flexDirection: 'row-reverse',
              alignItems: 'flex-start',
              height: 'auto',
              minHeight: 28,
              justifyContent: 'flex-end',
              gap: feedbackReasonGap,
            }}
            onChange={(checked) => handleReasonChange(checked, option.value)}
          />
        ))}
      </View>
      <TextInput
        value={comment}
        onChangeText={setComment}
        placeholder={t('app-review-feedback-placeholder')}
        placeholderTextColor={mutedColor}
        multiline
        maxLength={1000}
        style={{
          minHeight: 108,
          marginTop: 18,
          paddingHorizontal: 14,
          paddingVertical: 12,
          borderRadius: 8,
          borderWidth: 1,
          borderColor: theme.borderColorLight,
          color: bodyColor,
          backgroundColor: theme.background.primary,
          fontSize: 15,
          lineHeight: 21,
          textAlignVertical: 'top',
          outlineStyle: 'none' as any,
        }}
      />
      <View style={{ gap: 12, marginTop: 20 }}>
        <CustomButton title={t('app-review-send-feedback')} onPress={handleSendFeedback} disabled={isSubmitting} style={{ width: '100%' }} />
        <CustomButton secondaryColorLight title={t('app-review-contact-support')} onPress={handleContactSupport} disabled={isSubmitting} style={{ width: '100%' }} />
        <CustomButton
          secondaryColorLightOutline
          title={t('app-review-not-now')}
          onPress={handleClose}
          disabled={isSubmitting}
          style={{ width: '100%' }}
        />
      </View>
    </>
  );

  const renderPositiveStep = () => (
    <>
      {renderTitle(t('app-review-positive-title'))}
      {renderBody(t('app-review-positive-body'))}
      {renderReviewButtons()}
      <CustomButton
        secondaryColorLightOutline
        title={t('app-review-not-now')}
        onPress={handleClose}
        disabled={isSubmitting}
        style={{ width: '100%', marginTop: 12 }}
      />
    </>
  );

  const renderFeedbackSentStep = () => (
    <>
      {renderTitle(t('app-review-feedback-sent-title'))}
      {renderBody(t('app-review-feedback-sent-body'), 4)}
      <CustomButton title={t('got-it')} onPress={props.onClose} style={{ width: '100%', marginTop: 24 }} />
    </>
  );

  return (
    <ScrollView
      style={{ flexGrow: 0, width: popupWidth, maxHeight: popupMaxHeight, backgroundColor: cardBackgroundColor }}
      contentContainerStyle={{ paddingHorizontal: isCompact ? 22 : 28, paddingVertical: isCompact ? 24 : 30, backgroundColor: cardBackgroundColor }}
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled
      bounces={false}
      alwaysBounceVertical={false}
      alwaysBounceHorizontal={false}
      overScrollMode="never"
    >
      {step === 'rating' && renderRatingStep()}
      {step === 'feedback' && renderFeedbackStep()}
      {step === 'positive' && renderPositiveStep()}
      {step === 'feedbackSent' && renderFeedbackSentStep()}
    </ScrollView>
  );
};

export default AppReviewPromptPopup;
